import { PORTABLE_RECORDING_REF_PATTERN } from "@rekonas/domain";
import { authorizedFetch, parseResponse } from "./api";

type ReportData = unknown;

export interface OperationalReport {
  report_ref: string;
  recording_ref: string;
  created: string;
  source: string;
  data: ReportData;
}

function reportRoute(recordingRef: string, suffix: string): string {
  if (!PORTABLE_RECORDING_REF_PATTERN.test(recordingRef)) throw new TypeError("Invalid recording reference");
  return `/platform/mobile/v1/recordings/${encodeURIComponent(recordingRef)}${suffix}`;
}

function encodeBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 16_384) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 16_384));
  }
  return btoa(binary);
}

/** Internal/demo producer. The PDF and JSON source files are retained verbatim. */
export async function importOperationalReport(recordingRef: string, pdf: File, json: File): Promise<{ report_ref: string }> {
  if (!pdf.size || pdf.size > 16 * 1024 * 1024 || !json.size || json.size > 2 * 1024 * 1024) {
    throw new TypeError("PDF or JSON file is empty or exceeds the report import limit");
  }
  const body = {
    pdf_base64: encodeBase64(await pdf.arrayBuffer()),
    json_base64: encodeBase64(await json.arrayBuffer()),
  };
  return parseResponse<{ report_ref: string }>(await authorizedFetch(reportRoute(recordingRef, "/reports"), {
    method: "POST", body: JSON.stringify(body),
  }));
}

export async function getOperationalReport(recordingRef: string, signal?: AbortSignal): Promise<OperationalReport | null> {
  const response = await authorizedFetch(reportRoute(recordingRef, "/report"), { signal });
  if (response.status === 404) return null;
  const value = await parseResponse<unknown>(response);
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new TypeError("Invalid report wrapper");
  const row = value as Record<string, unknown>;
  if (typeof row.report_ref !== "string" || typeof row.recording_ref !== "string" ||
      row.recording_ref !== recordingRef || typeof row.created !== "string" ||
      typeof row.source !== "string" || !row.source || !("data" in row)) throw new TypeError("Invalid report wrapper");
  return row as unknown as OperationalReport;
}

export async function downloadOperationalReportPdf(recordingRef: string): Promise<Blob> {
  const response = await authorizedFetch(reportRoute(recordingRef, "/report/pdf"));
  if (!response.ok) await parseResponse(response);
  if (response.headers.get("content-type")?.split(";", 1)[0] !== "application/pdf") {
    throw new TypeError("Invalid report PDF content type");
  }
  return response.blob();
}

export interface OperationalChannelData {
  samples: Float32Array;
  sampleRateHz: number;
  sampleCount: number;
  unit: string;
  channel: string;
  dataVersion: string;
}

const nativeLittleEndian = new Uint8Array(new Uint16Array([1]).buffer)[0] === 1;

/** One native-rate numerical selector, streamed over the wire as float32-le. */
export async function fetchOperationalChannel(recordingRef: string, selector: string,
  signal?: AbortSignal): Promise<OperationalChannelData> {
  if (!selector || selector.includes(",")) throw new TypeError("Select exactly one numerical channel");
  const query = new URLSearchParams({ selector });
  const response = await authorizedFetch(`${reportRoute(recordingRef, "/signals/channel")}?${query}`, { signal });
  if (!response.ok) await parseResponse(response);
  const header = (name: string) => response.headers.get(name);
  const count = Number(header("X-Neoma-Sample-Count"));
  const rate = Number(header("X-Neoma-Sample-Rate-Hz"));
  const channel = header("X-Neoma-Channel");
  const unit = header("X-Neoma-Unit");
  const dataVersion = header("X-Neoma-Data-Version");
  if (header("X-Neoma-Dtype") !== "float32-le" || !Number.isSafeInteger(count) || count < 1 ||
      !Number.isFinite(rate) || rate <= 0 || !channel || !unit || !dataVersion ||
      header("X-Neoma-Gap-Value") !== "NaN" || header("X-Neoma-Start-Seconds") !== "0") {
    throw new TypeError("Invalid numerical channel metadata");
  }
  const buffer = await response.arrayBuffer();
  if (buffer.byteLength !== count * 4) throw new TypeError("Incomplete numerical channel stream");
  const samples = nativeLittleEndian ? new Float32Array(buffer) :
    Float32Array.from({ length: count }, (_, index) => new DataView(buffer).getFloat32(index * 4, true));
  return { samples, sampleRateHz: rate, sampleCount: count,
    unit: decodeURIComponent(unit), channel: decodeURIComponent(channel), dataVersion };
}
